import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardHeader from '../../components/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import '../../App.css';
import client from '../../api/client';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

function UpdateAdminPassword() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    } else if (formData.newPassword.length > 255) {
      newErrors.newPassword = 'Password must not exceed 255 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    

    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    if (!user?.professionalId) {
      toast.error('Unable to identify admin user. Please login again.');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        userID: user.professionalId,
        newPassword: formData.newPassword
      };

      const response = await client.put('/api/users/admin/password', payload);

      if (response.status === 200) {
        toast.success('Password updated successfully!');
        // Reset form
        setFormData({
          newPassword: '',
          confirmPassword: ''
        });
        // Navigate back to admin dashboard
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);
      }
    } catch (err) {
      console.error('Update password error:', err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to update password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <DashboardHeader userType="admin" userName={user?.displayName || 'Admin'} />

      <main className="dashboard-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="card shadow">
                <div className="card-body p-4">
                  <h2 className="card-title text-center mb-4">Update Admin Password</h2>

                  <form onSubmit={handleSubmit}>
                    {/* New Password Field */}
                    <div className="mb-4">
                      <label htmlFor="newPassword" className="form-label fw-bold">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                          id="newPassword"
                          name="newPassword"
                          placeholder="Enter new password (min. 6 characters)"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        {errors.newPassword && (
                          <div className="invalid-feedback d-block">{errors.newPassword}</div>
                        )}
                      </div>
                      <small className="text-muted">Minimum 6 characters required</small>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="mb-4">
                      <label htmlFor="confirmPassword" className="form-label fw-bold">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock />
                        </span>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Re-enter password"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                        {errors.confirmPassword && (
                          <div className="invalid-feedback d-block">{errors.confirmPassword}</div>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Updating...' : 'Update Password'}
                      </button>
                    </div>
                  </form>

                  {/* Back Link */}
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => navigate('/admin/dashboard')}
                      disabled={isLoading}
                    >
                      Back to Admin Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default UpdateAdminPassword;
